import { useState, useEffect, useContext, useRef } from "react"
import Context from "../../context/Context";
import { Box, Stack, Paper, Typography, Tabs, Tab } from "@mui/material"
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import { useLocation } from "react-router-dom";
import { getInstrumentSummary } from "../../api/app/instrumentRequests";
import { getRangedHistoricPricePoints, getInitialHistoricPricePoints } from "../../api/app/historicRequests";
import { getDateCorrespondingToRange } from "../../utils/chart";
import { historicPlottingRanges, chartTypeToggleOptions } from "../../constants/plottingConstants"
import { MultiAxisAnalysisChart, AnalysisCards, AnalysisTable } from "./components"
import CompoundHeader from "../../components/CompoundHeader";
import { bondNameFormatter } from "../../utils/formatters"
import { tabA11yPropInjector } from "../../utils/accessibility"
import SearchBarButton from "../../components/SearchBarButton";
import NoResults from "../../components/NoResults";
import TabPanelWrapper from "../../components/TabPanelWrapper";
import SearchSelectModal from "../../components/searchSelectModal/SearchSelectModal";

const OmniChart = () => {

    const { state } = useLocation()

    const initialInstrumentId = state?.instrumentId ?? null;

    const { showSnackbar } = useContext(Context)

    const chartRef = useRef(null);

    const [instruments, setInstruments] = useState([])

    const [currentTabIndex, setCurrentTabIndex] = useState(0)

    const [historicDatasets, setHistoricDatasets] = useState([])
    const [plottingHistoricPoints, setPlottingHistoricPoints] = useState([])

    const chartRangeToggleOptions = historicPlottingRanges.map(range => ({ label: range, value: range }))
    const [plottingRange, setPlottingRange] = useState(historicPlottingRanges.at(0))
    const [plottingType, setPlottingType] = useState(chartTypeToggleOptions.at(0).value)

    const [showSearchSelectModal, setShowSearchSelectModal] = useState(false)

    const clearState = () => {
        setInstruments([])
        setHistoricDatasets([])
        setPlottingHistoricPoints([])
    }

    const removeHistoricDataset = (isin) => {
        setHistoricDatasets(prev => prev.filter(dataset => dataset.id !== isin))
        setInstruments(prev => prev.filter(instrument => instrument.isin !== isin))
    }

    const filterHistoricDatasetsByRange = () => {
        if (plottingRange === "MAX") {
            setPlottingHistoricPoints(historicDatasets)
        } else {
            const targetLimitDate = instruments.map(instrument => new Date(getDateCorrespondingToRange(instrument.lastSessionDate, instrument.firstSessionDate, plottingRange))).sort((a, b) => a - b).at(-1);
            const rangedPlottingPoints = historicDatasets.map(dataset => ({
                ...dataset,
                points: dataset.points.filter(point => point.sessionDate >= targetLimitDate)
            }))
            setPlottingHistoricPoints(rangedPlottingPoints)
        }

    }

    const handleIncludeInstrument = (instrumentId) => {
        if (!instruments.some(i => i.isin === instrumentId)) {
            getInstrumentSummary(instrumentId)
                .then((instrumentSummary) => {
                    setInstruments(prev => [...prev, instrumentSummary])
                    setShowSearchSelectModal(false)
                })
                .catch(e => {
                    showSnackbar(e.message, 'warning');
                })
        } else {
            showSnackbar(`Instrument already in list: ${instrumentId}`, 'success');
        }
    }

    const handlePlottingRangeChangeHandler = (event, range) => {
        if (range) {
            setPlottingRange(range);
        }
    }

    const handlePlottingTypeChangeHandler = (event, type) => {
        if (type) {
            setPlottingType(type);
        }
    }

    const toggleSearchSelectModalVisibility = () => {
        setShowSearchSelectModal(prev => !prev)
    }

    const handleTabChange = (event, newTabIndex) => {
        setCurrentTabIndex(newTabIndex)
    }

    const areHistoricPointsAvailable = () => {
        return plottingHistoricPoints.some(
            dataset => dataset.points && dataset.points.length > 0
        )
    }

    const handleSortSync = (newInstrumentsArrange) => {
        const areListsSynced = newInstrumentsArrange.every((instrument, index) => instrument.isin === historicDatasets[index]?.id);

        let orderedListB = historicDatasets;

        if (!areListsSynced) {
            const mapHistoricDatasets = new Map(historicDatasets.map(dataset => [dataset.id, dataset]));
            orderedListB = newInstrumentsArrange.map(instrument => mapHistoricDatasets.get(instrument.isin)).filter(Boolean);
            setHistoricDatasets(orderedListB)
        }
    }


    useEffect(() => {
        if (instruments.length > 0) {
            if (instruments.length === 1 && historicDatasets.length === 0) {
                const singleInstrument = instruments.at(0)
                getInitialHistoricPricePoints(singleInstrument.isin, singleInstrument.lastSessionDate, 5)
                    .then((initialPricePoints) => {
                        setHistoricDatasets(prev => [...prev, { id: singleInstrument.isin, name: bondNameFormatter(singleInstrument), points: initialPricePoints }])
                    })
                    .catch(e => {
                        showSnackbar(e.message, 'warning');
                    })
            } else {
                const existingIds = historicDatasets.map(dataset => dataset.id);
                const missingHistoricDatasets = instruments.filter(instrument => !existingIds.includes(instrument.isin));
                missingHistoricDatasets.forEach((missingInstrumet) => {
                    const currentLimitDate = new Date(missingInstrumet.lastSessionDate)
                    const targetLimitDate = new Date(getDateCorrespondingToRange(missingInstrumet.lastSessionDate, missingInstrumet.firstSessionDate, plottingRange))
                    getRangedHistoricPricePoints(missingInstrumet.isin, targetLimitDate, currentLimitDate)
                        .then((pricePoints) => {
                            setHistoricDatasets(prev => [...prev, { id: missingInstrumet.isin, name: bondNameFormatter(missingInstrumet), points: pricePoints }])
                        })
                        .catch(e => {
                            showSnackbar(e.message, 'warning');
                        })
                })
            }
        }

    }, [instruments])

    useEffect(() => {
        instruments.forEach(instrument => {
            const currentLimitDate = historicDatasets.filter(dataset => dataset.id === instrument.isin).at(0).points.at(0).sessionDate
            const targetLimitDate = new Date(getDateCorrespondingToRange(instrument.lastSessionDate, instrument.firstSessionDate, plottingRange))
            if (targetLimitDate < currentLimitDate) {
                getRangedHistoricPricePoints(instrument.isin, targetLimitDate, currentLimitDate).then((rangedPoints) => {
                    setHistoricDatasets(prevItems =>
                        prevItems.map(item =>
                            item.id === instrument.isin
                                ? { ...item, points: [...rangedPoints, ...item.points] }
                                : item
                        )
                    )
                }).catch(e => {
                    showSnackbar(e.message, 'warning');
                })
            } else {
                filterHistoricDatasetsByRange()
            }
        })

    }, [plottingRange])

    useEffect(() => {
        filterHistoricDatasetsByRange()
    }, [historicDatasets])

    useEffect(() => {
        if (initialInstrumentId) {
            clearState()
            getInstrumentSummary(initialInstrumentId).then((initialInstrument) => { setInstruments(prev => [...prev, initialInstrument]) }).catch(e => {
                showSnackbar(e.message, 'warning');
            })
        }
    }, [initialInstrumentId])

    return (
        <Stack gap={8}>
            <CompoundHeader title="OmniChart" subtitle="Unlock a deeper understanding" />
            <Box sx={{ display: "flex", width: { xs: '100%', md: '50%' } }}>
                <SearchBarButton label="ISIN, issuer or issuer country" onClickHandler={toggleSearchSelectModalVisibility} />
            </Box>
            <Stack gap={4}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={currentTabIndex} onChange={handleTabChange} aria-label="Omnichar analysis tabs">
                        <Tab label="Chart" {...tabA11yPropInjector(0)} />
                        <Tab label="Table" {...tabA11yPropInjector(1)} />
                    </Tabs>
                </Box>
                {areHistoricPointsAvailable() ?
                    <>
                        <TabPanelWrapper value={currentTabIndex} index={0}>
                            <Stack gap={2}>
                                <Typography variant="subtitle1" fontWeight="bold">Trading chart</Typography>
                                <AnalysisCards
                                    historicDatasets={plottingHistoricPoints}
                                    plottingType={plottingType}
                                    chartTimeRange={plottingRange}
                                    handleRemoveAnalysisCard={removeHistoricDataset}
                                    chartRef={chartRef}
                                />
                                <Stack flex={{ xs: 1, lg: 0.75 }} gap={{ xs: 1, sm: 2 }} component={Paper} variant="outlined" sx={{ p: 2 }}>
                                    <Typography variant="subtitle1" fontWeight="bold">Trading chart</Typography>
                                    <MultiAxisAnalysisChart
                                        plottingDatasets={plottingHistoricPoints}
                                        plottingRange={plottingRange}
                                        handlePlottingRangeChangeHandler={handlePlottingRangeChangeHandler}
                                        chartRangeToggleOptions={chartRangeToggleOptions}
                                        plottingType={plottingType}
                                        handlePlottingTypeChangeHandler={handlePlottingTypeChangeHandler}
                                        chartTypeToggleOptions={chartTypeToggleOptions}
                                        chartRef={chartRef}
                                    />
                                </Stack>
                            </Stack>

                        </TabPanelWrapper>
                        <TabPanelWrapper value={currentTabIndex} index={1}>
                            <AnalysisTable instruments={instruments} handleSortSync={handleSortSync} removeInstrument={removeHistoricDataset} />
                        </TabPanelWrapper>
                    </>
                    :
                    <NoResults Icon={QueryStatsIcon} title="Start building your analysis" description="Use the search bar to add instruments and unlock insights." />

                }
            </Stack>

            <SearchSelectModal open={showSearchSelectModal} onClose={toggleSearchSelectModalVisibility} onSelect={handleIncludeInstrument} displayHotLinks={false} />
        </Stack >
    )
}

export default OmniChart;